import { supabase } from './supabase';
import { t } from '../data/translations';

export interface WishlistItem {
  id: string;
  name: string;
  link?: string;
  reserved_by?: string;
  secret_key?: string;
  created_at?: string;
}

export interface WishlistServiceResponse {
  success: boolean;
  data?: WishlistItem | WishlistItem[];
  error?: string;
}

class WishlistService {
  async getAllItems(): Promise<WishlistServiceResponse> {
    try {
      const { data, error } = await supabase
        .from('wishlist')
        .select('*');

      if (error) {
        console.error('Wishlist fetch error:', error);
        return {
          success: false,
          error: 'Failed to fetch wishlist'
        };
      }

      return {
        success: true,
        data: data as WishlistItem[]
      };

    } catch (error) {
      console.error('Wishlist fetch error:', error);
      return {
        success: false,
        error: 'Failed to fetch wishlist'
      };
    }
  }

  async reserveItem(itemId: string, userName: string, secretKey: string): Promise<WishlistServiceResponse> {
    try {
      const { data, error } = await supabase
        .from('wishlist')
        .update({
          reserved_by: userName,
          secret_key: secretKey
        })
        .eq('id', itemId)
        .is('reserved_by', null) // Только если еще не забронировано
        .select()
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows updated - item already reserved
          return {
            success: false,
            error: 'Этот подарок уже забронирован'
          };
        }
        console.error('Wishlist reservation error:', error);
        return {
          success: false,
          error: 'Failed to reserve item'
        };
      }

      return {
        success: true,
        data
      };

    } catch (error) {
      console.error('Wishlist reservation error:', error);
      return {
        success: false,
        error: 'Failed to reserve item'
      };
    }
  }

  async cancelReservation(itemId: string, secretKey: string): Promise<WishlistServiceResponse> {
    try {
      const { data, error } = await supabase
        .from('wishlist')
        .update({
          reserved_by: null,
          secret_key: null
        })
        .eq('id', itemId)
        .eq('secret_key', secretKey) // Только владелец брони может отменить
        .select()
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return {
            success: false,
            error: 'Не удалось отменить бронь. Проверьте секретный ключ.'
          };
        }
        console.error('Wishlist cancellation error:', error);
        return {
          success: false,
          error: 'Failed to cancel reservation'
        };
      }

      return {
        success: true,
        data
      };

    } catch (error) {
      console.error('Wishlist cancellation error:', error);
      return {
        success: false,
        error: 'Failed to cancel reservation'
      };
    }
  }

  async addItem(name: string, link?: string): Promise<WishlistServiceResponse> {
    try {
      const { data, error } = await supabase
        .from('wishlist')
        .insert([{ name, link }])
        .select()
        .single();

      if (error) {
        console.error('Wishlist add error:', error);
        return {
          success: false,
          error: 'Failed to add item'
        };
      }

      return {
        success: true,
        data
      };

    } catch (error) {
      console.error('Wishlist add error:', error);
      return {
        success: false,
        error: 'Failed to add item'
      };
    }
  }

  async removeItem(itemId: string): Promise<WishlistServiceResponse> {
    try {
      const { error } = await supabase
        .from('wishlist')
        .delete()
        .eq('id', itemId);

      if (error) {
        console.error('Wishlist remove error:', error);
        return {
          success: false,
          error: 'Failed to remove item'
        };
      }

      return {
        success: true
      };

    } catch (error) {
      console.error('Wishlist remove error:', error);
      return {
        success: false,
        error: 'Failed to remove item'
      };
    }
  }

  // Проверяет, забронировал ли пользователь конкретный подарок
  async isReservedByUser(itemId: string, secretKey: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('wishlist')
        .select('secret_key')
        .eq('id', itemId)
        .eq('secret_key', secretKey)
        .single();

      return !error && data !== null;
    } catch (error) {
      return false;
    }
  }
}

export const wishlistService = new WishlistService(); 